import { useContext } from 'react'

import { useForm, useLatestEntity } from '@domain-group/react-hooks'
import { digObject, launchModal } from '@domain-group/javascript-utils'
import type { UseFormOptions } from '@domain-group/react-hooks'

import formatDate from '@functions/formatDate'
import { baseURL } from '@functions/api'

import useDispatch from '@hooks/useDispatch'
import useReduxAction from '@hooks/useReduxAction'
import useSelector from '@hooks/useSelector'

import * as providerPriceListActions from '@redux/modules/providerPriceList'

import defaultFormState, { requiredFields } from '@models/providerPriceList'

import PageContext from '@contexts/pageContext'

import type { AppDispatch } from '@redux/store'
import type { BulkUpdateParams } from '@redux/modules/providerPriceList'
import type { DeleteParams, ModuleState } from '@redux/modules/types'
import type { ProviderPriceListModel, ProviderPriceListRequestOptions } from '@models/types'

export type GenerateUrlsOptions = {
  prefixRoute: string,
}

export const generateUrls = (providerPriceList?: Partial<ProviderPriceListModel>, options?: GenerateUrlsOptions) => {
  const { id, public_token } = providerPriceList || {}
  const { prefixRoute = 'systemManager' } = options || {}

  return {
    archivedProviderPriceListsIndexUrl: `#/${prefixRoute}/providerPriceLists?hidden=true`,
    downloadCsvUrl: `${baseURL}/provider_price_lists/${id}/export_csv.xlsx?public_token=${public_token}`,
    editProviderPriceListItemsEditorUrl: `#/${prefixRoute}/providerPriceLists/${id}/edit/items?viewMode=editor`,
    editProviderPriceListItemsMatrixUrl: `#/${prefixRoute}/providerPriceLists/${id}/edit/items?viewMode=matrix`,
    editProviderPriceListUrl: `#/${prefixRoute}/providerPriceLists/${id}/edit`,
    editProviderPriceListRelationsUrl: `#/${prefixRoute}/providerPriceLists/${id}/edit/relations`,
    masterProviderPriceListsIndexUrl: `#/${prefixRoute}/providerPriceLists?hidden=false&is_master=true`,
    providerPriceListsIndexUrl: `#/${prefixRoute}/providerPriceLists?hidden=false&is_master=false`,
  }
}

type CreateProviderPriceListParams = {
  dispatch: AppDispatch,
  providerPriceListParams: Partial<ProviderPriceListModel>,
  requestOptions?: ProviderPriceListRequestOptions,
}

const createProviderPriceList = (params: CreateProviderPriceListParams) => {
  const { dispatch, providerPriceListParams, requestOptions } = params
  const { createProviderPriceList: createFn } = providerPriceListActions

  return dispatch(createFn(providerPriceListParams, requestOptions))
}

type DeleteItemsForProductParams = {
  dispatch: AppDispatch,
  providerPriceList: Partial<ProviderPriceListModel>,
  requestOptions?: ProviderPriceListRequestOptions,
  serviceProductId: number,
}

const deleteItemsForProduct = (params: DeleteItemsForProductParams) => {
  const {
    dispatch, providerPriceList, requestOptions, serviceProductId,
  } = params
  const { deleteItemsForProduct: deleteFn } = providerPriceListActions

  const deleteItemsParams = {
    providerPriceListId: providerPriceList.id,
    serviceProductId,
  }

  // @ts-expect-error TS2345
  return dispatch(deleteFn(deleteItemsParams, requestOptions))
}

type ForkProviderPriceListParams = {
  dispatch: AppDispatch,
  forkParams: {
    agencyId?: number,
    hide_items?: boolean,
    title: string,
  },
  providerPriceList: Partial<ProviderPriceListModel>,
  requestOptions?: ProviderPriceListRequestOptions,
}

const forkProviderPriceList = (params: ForkProviderPriceListParams) => {
  const {
    dispatch, forkParams, providerPriceList, requestOptions,
  } = params
  const { forkProviderPriceList: forkFn } = providerPriceListActions

  const forkPriceListParams = {
    providerPriceListId: providerPriceList.id,
    ...forkParams,
  }

  // @ts-expect-error TS2345
  return dispatch(forkFn(forkPriceListParams, requestOptions))
}

type DuplicateProviderPriceListParams = {
  dispatch: AppDispatch,
  providerPriceList: ProviderPriceListModel,
  requestOptions?: ProviderPriceListRequestOptions,
}

const duplicateProviderPriceList = (params: DuplicateProviderPriceListParams) => {
  const { dispatch, providerPriceList, requestOptions } = params
  const { duplicateProviderPriceList: duplicateFn } = providerPriceListActions

  return dispatch(duplicateFn(providerPriceList, requestOptions)).then((response) => {
    const { success } = response

    if (success){
      const { data } = response
      const { editProviderPriceListUrl } = generateUrls(data)

      return {
        success,
        data,
        redirectUrl: editProviderPriceListUrl,
      }
    }

    return response
  })
}

type UnarchiveProviderPriceListParams = {
  dispatch: AppDispatch,
  providerPriceList: ProviderPriceListModel,
  requestOptions?: ProviderPriceListRequestOptions,
}

const unarchiveProviderPriceList = (params: UnarchiveProviderPriceListParams) => {
  const { dispatch, providerPriceList, requestOptions } = params
  const { unarchiveProviderPriceList: unarchiveFn } = providerPriceListActions

  return dispatch(unarchiveFn(providerPriceList, requestOptions))
}

type UpdateProviderPriceListParams = {
  dispatch: AppDispatch,
  providerPriceList: ProviderPriceListModel,
  providerPriceListParams: Partial<ProviderPriceListModel>,
  requestOptions?: ProviderPriceListRequestOptions,
}

const updateProviderPriceList = (params: UpdateProviderPriceListParams) => {
  const {
    dispatch, providerPriceList, providerPriceListParams, requestOptions,
  } = params
  const { updateProviderPriceList: updateFn } = providerPriceListActions

  const updatedParams = {
    id: providerPriceList.id,
    ...providerPriceListParams,
  }

  return dispatch(updateFn(updatedParams, requestOptions))
}

type SyncChangesToForksParams = {
  dispatch: AppDispatch,
  providerPriceList: ProviderPriceListModel,
  requestOptions?: ProviderPriceListRequestOptions,
}

const syncChangesToForks = (params: SyncChangesToForksParams) => {
  const { dispatch, providerPriceList, requestOptions } = params
  const { syncChangesToForks: syncFn } = providerPriceListActions

  return dispatch(syncFn(providerPriceList, requestOptions))
}

type DeletePriceListParams = { next_price_list_id: number }

type DeleteProviderPriceListParams = {
  dispatch: AppDispatch,
  providerPriceList: DeleteParams<ProviderPriceListModel>,
  providerPriceListParams: DeletePriceListParams,
  requestOptions?: ProviderPriceListRequestOptions,
}

const deleteProviderPriceList = (params: DeleteProviderPriceListParams) => {
  const {
    dispatch, providerPriceList, providerPriceListParams, requestOptions,
  } = params
  const { deleteProviderPriceList: deleteFn } = providerPriceListActions

  const updatedParams = {
    id: providerPriceList.id,
    ...providerPriceListParams,
  }

  return dispatch(deleteFn(updatedParams, requestOptions))
}

export type BulkUpdateProviderPriceListsParams = {
  dispatch: AppDispatch,
  bulkUpdateParams: BulkUpdateParams,
  requestOptions?: ProviderPriceListRequestOptions,
}

const bulkUpdateProviderPriceLists = (params: BulkUpdateProviderPriceListsParams) => {
  const { dispatch, bulkUpdateParams, requestOptions } = params
  const { bulkUpdateProviderPriceLists: bulkUpdateFn } = providerPriceListActions

  return dispatch(bulkUpdateFn(bulkUpdateParams, requestOptions))
}

type BulkReassignAgenciesParams = {
  dispatch: AppDispatch,
  providerPriceListParams: Partial<ProviderPriceListModel>,
  requestOptions?: ProviderPriceListRequestOptions,
}

const bulkReassignAgencies = (params: BulkReassignAgenciesParams) => {
  const {
    dispatch, providerPriceListParams, requestOptions,
  } = params

  const { bulkReassignAgencies: bulkReassignFn } = providerPriceListActions

  return dispatch(bulkReassignFn(providerPriceListParams, requestOptions))
}

type adjustPriceByPercentageParams = {
  dispatch: AppDispatch,
  adjustPriceParams: {
    percentage: number,
    undo_change: boolean,
    round_to_nearest_dollar: boolean,
  },
  providerPriceList: Partial<ProviderPriceListModel>,
  requestOptions?: ProviderPriceListRequestOptions,
}

const adjustPriceByPercentage = (params: adjustPriceByPercentageParams) => {
  const {
    dispatch, adjustPriceParams, providerPriceList, requestOptions,
  } = params

  const { adjustPriceByPercentage: adjustPriceFn } = providerPriceListActions
  const priceAdjustParams = {
    providerPriceListId: providerPriceList.id,
    ...adjustPriceParams,
  }

  // @ts-expect-error TS2345
  return dispatch(adjustPriceFn(priceAdjustParams, requestOptions))
}

type CustomFormOptions = {
  customRequiredFields?: UseFormOptions['requiredFields'],
}

export function useProviderPriceListForm(
  providerPriceList: Partial<ProviderPriceListModel>,
  options: UseFormOptions & CustomFormOptions = {},
) {
  const { customRequiredFields = [], validateOn } = options || {}

  const expiresAtString = digObject(providerPriceList, 'dates.expiry.date', '')
  const expires_at = formatDate(expiresAtString, 'dd/MM/yyyy', 'yyyy-MM-dd')

  const providerPriceListForm = useForm(
    defaultFormState,
    {
      entity: { ...providerPriceList, expires_at },
      requiredFields: [...requiredFields, ...customRequiredFields],
      validateOn,
    },
    [providerPriceList.id, providerPriceList.cache_key],
  )

  return {
    ...providerPriceListForm,
  }
}

export const useRelations = (providerPriceList: Partial<ProviderPriceListModel> = {}) => {
  const { master_price_list_id, provider_id, service_id } = providerPriceList || {}

  const entities = useSelector(reduxState => reduxState.entities)
  const { providerPriceLists, providers, services } = entities

  const masterPriceList = master_price_list_id && providerPriceLists[master_price_list_id]
    ? providerPriceLists[master_price_list_id]
    : {}
  const provider = provider_id && providers[provider_id] ? providers[provider_id] : {}
  const service = service_id && services[service_id] ? services[service_id] : {}

  return {
    masterPriceList,
    provider,
    service,
  }
}

type CustomPayload = {
  priceListIds?: number[],
}

type Options = {
  performHttpRequests?: boolean,
  requestOptions?: ProviderPriceListRequestOptions,
}

function useProviderPriceList(initEntity: Partial<ProviderPriceListModel> = {}, options: Options = {}) {
  const { performHttpRequests, requestOptions } = options

  // @ts-expect-error TS2322
  const {
    entity: providerPriceList,
  // @ts-expect-error TS2345
  }: { entity: ProviderPriceListModel } = useLatestEntity(initEntity, 'providerPriceLists')
  const { id, master_price_list_version } = providerPriceList

  const dispatch = useDispatch()

  const { callbacks } = useContext(PageContext)

  const { masterPriceList, provider, service } = useRelations(providerPriceList)
  const masterPriceListVersion = `${digObject(masterPriceList, 'dates.updated.integer')}`
  const isSyncedWithMaster = masterPriceListVersion === master_price_list_version

  useReduxAction(
    'providerPriceLists',
    'loadProviderPriceList',
    {
      entityId: id,
      ...requestOptions,
    },
    [id, performHttpRequests],
    {
      dispatchAction: (action, actionRequestOptions) => action(id, actionRequestOptions),
      shouldPerformFn: ({ loading }: ModuleState) => !!performHttpRequests && !loading,
    },
  )

  const {
    creating, deleting, loadedIds, loading, updating,
  } = useSelector(reduxState => reduxState.providerPriceLists)

  return {
    callbacks: {
      adjustPriceByPercentage: (
        adjustPriceParams: { percentage: number, undo_change: boolean, round_to_nearest_dollar: boolean },
        entityOptions?: ProviderPriceListRequestOptions,
      ) => (
        adjustPriceByPercentage({
          dispatch, adjustPriceParams, providerPriceList, requestOptions: entityOptions,
        })
      ),
      archiveProviderPriceList: (hasAgencyAssigned?: boolean) => (
        launchModal({
          callbacks,
          modalKey: 'ArchiveProviderPriceListModal',
          payload: { providerPriceList, hasAgencyAssigned },
        })
      ),
      bulkReassignAgencies: (
        providerPriceListParams: Partial<ProviderPriceListModel>,
        entityOptions?: ProviderPriceListRequestOptions,
      ) => (
        bulkReassignAgencies({
          dispatch, providerPriceListParams, requestOptions: entityOptions,
        })
      ),
      bulkUpdateProviderPriceLists: (
        bulkUpdateParams: BulkUpdateParams,
        entityOptions?: ProviderPriceListRequestOptions,
      ) => (
        bulkUpdateProviderPriceLists({ bulkUpdateParams, dispatch, requestOptions: entityOptions })
      ),
      createProviderPriceList: (
        providerPriceListParams: Partial<ProviderPriceListModel>,
        entityOptions?: ProviderPriceListRequestOptions,
      ) => (
        createProviderPriceList({ providerPriceListParams, dispatch, requestOptions: entityOptions })
      ),
      createOrEditProviderPriceList: () => launchModal({
        callbacks,
        modalKey: 'CreateOrEditProviderPriceListModal',
        payload: { providerPriceList },
      }),
      deleteItemsForProduct: (
        serviceProductId: number,
        entityOptions?: ProviderPriceListRequestOptions,
      ) => (
        deleteItemsForProduct({
          dispatch, providerPriceList, requestOptions: entityOptions, serviceProductId,
        })
      ),
      deleteProviderPriceList: (
        providerPriceListParams: DeletePriceListParams,
        entityOptions?: ProviderPriceListRequestOptions,
      ) => (
        deleteProviderPriceList({
          dispatch, providerPriceList, providerPriceListParams, requestOptions: entityOptions,
        })
      ),
      duplicateProviderPriceList: (entityOptions?: ProviderPriceListRequestOptions) => (
        duplicateProviderPriceList({ dispatch, providerPriceList, requestOptions: entityOptions })
      ),
      forkProviderPriceList: (
        forkParams: { agency_id?: number, hide_items?: boolean, title: string },
        entityOptions?: ProviderPriceListRequestOptions,
      ) => (
        forkProviderPriceList({
          dispatch, forkParams, providerPriceList, requestOptions: entityOptions,
        })
      ),
      launchBulkReassignAgencies: () => launchModal({
        callbacks,
        modalKey: 'BulkReassignAgenciesModal',
        payload: { providerPriceList },
      }),
      launchBulkUpdateProviderPriceLists: (customPayload: CustomPayload) => launchModal({
        callbacks,
        modalKey: 'BulkUpdateProviderPriceListsModal',
        payload: { providerPriceList, ...customPayload },
      }),
      launchForkProviderPriceList: () => launchModal({
        callbacks,
        modalKey: 'ForkProviderPriceListModal',
        payload: { providerPriceList },
      }),
      syncChangesToForks: (entityOptions?: ProviderPriceListRequestOptions) => (
        syncChangesToForks({
          dispatch,
          providerPriceList,
          requestOptions: entityOptions,
        })
      ),
      unarchiveProviderPriceList: (entityOptions?: ProviderPriceListRequestOptions) => (
        unarchiveProviderPriceList({ dispatch, providerPriceList, requestOptions: entityOptions })
      ),
      updateProviderPriceList: (
        providerPriceListParams: Partial<ProviderPriceListModel>,
        entityOptions?: ProviderPriceListRequestOptions,
      ) => (
        updateProviderPriceList({
          providerPriceList, providerPriceListParams, dispatch, requestOptions: entityOptions,
        })
      ),
    },
    creating,
    deleting,
    isSyncedWithMaster,
    loading: loading && !loadedIds.includes(providerPriceList.id),
    providerPriceList,
    provider,
    service,
    updating,
    urls: generateUrls(providerPriceList),
  }
}

export type UseProviderPriceListPayload = ReturnType<typeof useProviderPriceList>

export default useProviderPriceList
